import { memo, PropsWithChildren, ReactElement, useMemo, useState, useEffect } from 'react';
import {
    GridCellParams,
    GridColDef,
    GridRowId,
    GridRowSelectionModel,
    GridSortDirection,
    GridSortModel,
    GridValueGetterParams,
    MaterialDataGrid,
} from '@enterprise/material-data-grid';
import { LabelEntity } from 'apps/client/src/core/entity/LabelEntity';
import { PracticeInfo, PracticeStatus } from '../../../practiceConnections/MyOrganizationPageStore';
import styles from './selectPracticeRegionsGrid.module.scss';
import classNames from 'classnames';
import i18n from 'i18next';
import { SpotSearchbar } from '@enterprise/spot';
import { Image } from '../../../../components/helpers/Image';
import { head } from 'lodash';

export interface SelectedPracticeRegions {
    practices?: PracticeInfo[];
    regions?: LabelEntity[];
}

enum GridColumns {
    PracticeName = 'practiceName',
    RegionName = 'regionName',
}

const getPracticeName = ({ name, type }: PracticeInfo): string => {
    if (!name && type === PracticeStatus.Pending) {
        return i18n.t('myOrganization:defaultPendingPracticeName', 'Pending Practice Connection');
    }

    return String(name);
};

export type PracticeRegionGridProps = PropsWithChildren<{
    practices?: PracticeInfo[];
    regions?: LabelEntity[];
    isLoading?: boolean;
    onSelectionChange: (selectedPractices: PracticeInfo[]) => void;
}>;

export const SelectPracticesRegionsGrid = memo(function SelectPracticesRegionsGrid(props: PracticeRegionGridProps) {
    const { practices = [], regions = [], isLoading = false, onSelectionChange } = props;

    const practiceSortModel = useMemo<GridSortModel>(
        () => [
            {
                field: GridColumns.PracticeName,
                sort: 'asc' as GridSortDirection,
            },
        ],
        [],
    );

    const regionSortModel = useMemo<GridSortModel>(
        () => [
            {
                field: GridColumns.RegionName,
                sort: 'asc' as GridSortDirection,
            },
        ],
        [],
    );

    const PracticeNameCell = (params: GridCellParams): ReactElement => {
        const practice = params.row as PracticeInfo;
        const name = getPracticeName(practice);

        return (
            <div className={styles.cellContainer} data-automation-id="select-practices-grid-name-cell">
                <div className={styles.cellName}>
                    <div className={classNames('spot-typography__text--body', styles.name)} data-automation-id="select-practices-cell-name">
                        {name}
                    </div>
                </div>
            </div>
        );
    };

    const RegionNameCell = (params: GridCellParams): ReactElement => {
        const { name } = params.row as LabelEntity;
        return (
            <div className={styles.cellContainer} data-automation-id="select-regions-grid-name-cell">
                <div className={styles.cellName}>
                    <div className={classNames('spot-typography__text--body', styles.name)} data-automation-id="select-practices-cell-name">
                        {name}
                    </div>
                </div>
            </div>
        );
    };

    const AdditionalItemsCell = (params: GridCellParams): ReactElement => {
        const { name } = params.row as LabelEntity;
        return (
            <div className={styles.cellContainer} data-automation-id="select-all-practices-grid-name-cell">
                <div className={styles.cellName}>
                    <div className={classNames('spot-typography__text--body', styles.name)} data-automation-id="select-practices-cell-name">
                        {name}
                    </div>
                </div>
            </div>
        );
    };

    const practiceColumns = useMemo<GridColDef[]>(() => {
        return [
            {
                field: GridColumns.PracticeName,
                flex: 1,
                renderCell: PracticeNameCell,
                valueGetter: (params: GridValueGetterParams) => {
                    return getPracticeName(params.row as PracticeInfo);
                },
            },
        ];
    }, []);

    const regionColumns = useMemo<GridColDef[]>(() => {
        return [
            {
                field: GridColumns.RegionName,
                flex: 1,
                renderCell: RegionNameCell,
                valueGetter: (params: GridValueGetterParams) => {
                    const { name } = params.row as LabelEntity;
                    return name;
                },
            },
        ];
    }, []);

    const additionalColumns = useMemo<GridColDef[]>(() => {
        return [
            {
                field: 'name',
                flex: 1,
                renderCell: AdditionalItemsCell,
                valueGetter: (params: GridValueGetterParams) => {
                    const { name } = params.row as LabelEntity;
                    return name;
                },
            },
        ];
    }, []);

    const [practiceRows, setPracticeRows] = useState<PracticeInfo[]>(practices);
    const [regionRows, setRegionRows] = useState<LabelEntity[]>(regions);
    const [showSelectAll, setShowSelectAll] = useState<boolean>(true);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [selectedGridPractices, setSelectedGridPractices] = useState<PracticeInfo[]>([]);
    const [selectedGridRegions, setSelectedGridRegions] = useState<LabelEntity[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        if (!searchTerm) {
            setPracticeRows(practices);
            setRegionRows(regions);
            setShowSelectAll(true);
            return;
        }

        const newPractices = practices.filter(({ name }) => {
            return name.toLowerCase().includes(searchTerm.toLowerCase());
        });

        const newRegions = regions.filter(({ name }) => {
            return name.toLowerCase().includes(searchTerm.toLowerCase());
        });

        setShowSelectAll(false);
        setPracticeRows(newPractices);
        setRegionRows(newRegions);
    }, [searchTerm]);

    useEffect(() => {
        if (selectAll) {
            onSelectionChange(practices);
            return;
        }

        const selectedRegionPractices = selectedGridRegions.reduce((agg, { practices: regionPractices }) => {
            const selected = practices.filter(({ rowId }) => regionPractices.find((id) => String(id) === String(rowId)));
            return [...agg, ...selected];
        }, [] as PracticeInfo[]);
        const selectedPractices = new Set<PracticeInfo>([...selectedGridPractices, ...selectedRegionPractices]);

        onSelectionChange(Array.from(selectedPractices));
    }, [selectedGridRegions, selectedGridPractices, selectAll]);

    const onChangeRegions = (selectionModel: GridRowSelectionModel) => {
        const selected = new Set<LabelEntity>(selectedGridRegions);

        regionRows.forEach((region) => {
            if (selectionModel.includes(region.id)) {
                selected.add(region);
            } else {
                selected.delete(region);
            }
        });

        setSelectedGridRegions(Array.from(selected));

        if (selected.size) {
            setSelectAll(false);
        }
    };

    const onChangePractices = (selectionModel: GridRowSelectionModel) => {
        const selected = new Set<PracticeInfo>(selectedGridPractices);

        practiceRows.forEach((practice) => {
            if (selectionModel.includes(practice.id)) {
                selected.add(practice);
            } else {
                selected.delete(practice);
            }
        });

        setSelectedGridPractices(Array.from(selected));

        if (selected.size) {
            setSelectAll(false);
        }
    };

    const onChangeSelectAll = (selectionModel: GridRowSelectionModel) => {
        const isAllSelected = Boolean(head(selectionModel));

        setSelectAll(isAllSelected);

        if (isAllSelected) {
            setSelectedGridPractices([]);
            setSelectedGridRegions([]);
        }
    };

    const practiceSelectionModel = useMemo<GridRowId[] | undefined>(() => {
        return practiceRows.filter(({ id }) => selectedGridPractices.find((item) => item.id === id)).map(({ id }) => id);
    }, [selectedGridPractices, practiceRows]);

    const selectRegionModel = useMemo<GridRowId[] | undefined>(() => {
        return regionRows.filter(({ id }) => selectedGridRegions.find((item) => item.id === id)).map(({ id }) => id);
    }, [selectedGridRegions, regionRows]);

    const selectAllModel = selectAll ? [1] : [];

    return (
        <>
            <div className={styles.searchBarContainer} data-automation-id={'practiceRegionsSearchBar'}>
                <SpotSearchbar
                    className={classNames(styles.searchBar, 'spot-form spot-search-bar spot-search-bar--low-priority')}
                    onChange={(value) => setSearchTerm(value)}
                    value={''}
                    secondary={false}
                    isSelfControlled={true}
                />
            </div>
            <div>
                {!Boolean(practiceRows.length) && !Boolean(regionRows.length) && (
                    <div className={styles.noPracticesFound} data-automation-id="no-practices-regions-found">
                        <Image className={styles.noPracticesImage} url={'img/noResultsDog.png'} />
                        <span className={styles.noPracticesText}>
                            {i18n.t('controlCenter:itemSelectPractices:noPracticesFound', 'No Practices Found')}
                        </span>
                    </div>
                )}
                {Boolean(practiceRows.length) && showSelectAll && (
                    <div data-automation-id={'select-all-data-grid'}>
                        <MaterialDataGrid
                            className={classNames(styles.holder)}
                            loading={isLoading}
                            columns={additionalColumns}
                            rows={[{ name: `All Additional Practices (${practiceRows.length})`, id: 1 }]}
                            headerHeight={0}
                            rowHeight={60}
                            getRowClassName={() => styles.row}
                            autoHeight={true}
                            rowSelectionModel={selectAllModel}
                            checkboxSelection={true}
                            onRowSelectionModelChange={onChangeSelectAll}
                        />
                    </div>
                )}
                {Boolean(regionRows.length) && (
                    <div data-automation-id={'select-regions-data-grid'}>
                        <div className={styles.gridTitles}>{i18n.t('controlCenter:itemSelectPractices:regions', 'REGIONS')}</div>
                        <MaterialDataGrid
                            className={classNames(styles.holder)}
                            loading={isLoading}
                            columns={regionColumns}
                            rows={regionRows}
                            headerHeight={0}
                            rowHeight={50}
                            getRowClassName={() => styles.row}
                            autoHeight={true}
                            rowSelectionModel={selectRegionModel}
                            sortModel={regionSortModel}
                            checkboxSelection={true}
                            onRowSelectionModelChange={onChangeRegions}
                        />
                    </div>
                )}
                {Boolean(practiceRows.length) && (
                    <div data-automation-id={'select-practices-data-grid'}>
                        <div className={styles.gridTitles}>
                            {i18n.t('controlCenter:itemSelectPractices:individualPractices', 'INDIVIDUAL PRACTICES')}
                        </div>
                        <MaterialDataGrid
                            className={classNames(styles.holder)}
                            loading={isLoading}
                            columns={practiceColumns}
                            rows={practiceRows}
                            headerHeight={0}
                            rowHeight={60}
                            getRowClassName={() => styles.row}
                            autoHeight={true}
                            rowSelectionModel={practiceSelectionModel}
                            sortModel={practiceSortModel}
                            checkboxSelection={true}
                            onRowSelectionModelChange={onChangePractices}
                        />
                    </div>
                )}
            </div>
        </>
    );
});
